import { useMyContext } from 'Context/MyContextProvider';
import { Button, DatePicker, Modal, Result, Table, Tag, Tooltip, message } from 'antd';
import apiClient from 'services/apiClient';
import React, { useCallback, useEffect, useState } from 'react';
import {
    EyeOutlined,
    ClockCircleOutlined,
    PauseCircleOutlined,
    PlayCircleOutlined,
    StopOutlined,
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCheckDouble, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';

const { confirm } = Modal;

const normalizeStatusFilter = (filter) => {
    if (!filter || filter === 'pending') {
        return 'scheduled';
    }
    return filter;
};

const formatScheduleTime = (record) => {
    if (record?.schedule_time) {
        return dayjs(record.schedule_time).format('YYYY-MM-DD HH:mm');
    }

    const sendConfig = record?.send_config;
    if (sendConfig?.schedule_date && sendConfig?.schedule_time) {
        return `${sendConfig.schedule_date} | ${sendConfig.schedule_time}`;
    }

    return '-';
};

const getStatusColor = (status) => {
    if (status === 'scheduled') {
        return 'blue';
    }
    if (status === 'paused') {
        return 'gold';
    }
    if (status === 'cancelled') {
        return 'red';
    }
    if (status === 'processing') {
        return 'orange';
    }
    return 'default';
};

const isManageableSchedule = (status) => status === 'scheduled' || status === 'paused';

const ScheduleCampainTable = ({ limit, filter }) => {
    const { api, authToken, UserData } = useMyContext();
    const [listLoading, setListLoading] = useState(false);
    const [reportLoading, setReportLoading] = useState(false);
    const [campaigns, setCampaigns] = useState([]);
    const [scheduleDate, setScheduleDate] = useState('');
    const [scheduleTime, setScheduleTime] = useState('');
    const [editId, setEditId] = useState('');
    const [open, setOpen] = useState(false);
    const [campaignReport, setCampaignReport] = useState([]);
    const [reportPagination, setReportPagination] = useState({ current: 1, pageSize: 50, total: 0 });
    const [listPagination, setListPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [reportCampaignId, setReportCampaignId] = useState(null);
    const [openScheduleModel, setOpenSchedule] = useState(false);
    const [rescheduleLoading, setRescheduleLoading] = useState(false);

    const isDashboardWidget = Boolean(limit && filter);
    const listPageSize = isDashboardWidget ? (limit || 10) : listPagination.pageSize;

    const GetCampaignData = useCallback(async () => {
        if (!UserData?.id) {
            return;
        }

        setListLoading(true);
        try {
            const params = isDashboardWidget
                ? { status: 'scheduled', is_scheduled: '1', page: 1, per_page: listPageSize }
                : {
                    schedule_pending: '1',
                    page: listPagination.current,
                    per_page: listPagination.pageSize,
                };

            const response = await apiClient.get(`${api}campaigns/${UserData.id}`, {
                headers: {
                    Authorization: 'Bearer ' + authToken,
                },
                params,
            });

            setCampaigns(Array.isArray(response.data.campaign) ? response.data.campaign : []);

            if (!isDashboardWidget && response.data.pagination) {
                setListPagination((prev) => ({
                    ...prev,
                    total: response.data.pagination.total,
                }));
            }
        } catch (error) {
            message.error('Failed to load scheduled campaigns');
        } finally {
            setListLoading(false);
        }
    }, [
        UserData?.id,
        api,
        authToken,
        isDashboardWidget,
        listPageSize,
        listPagination.current,
        listPagination.pageSize,
    ]);

    useEffect(() => {
        GetCampaignData();
    }, [GetCampaignData]);

    const handleListTableChange = (tablePagination) => {
        setListPagination((prev) => ({
            ...prev,
            current: tablePagination.current,
            pageSize: tablePagination.pageSize,
        }));
    };

    const tableColumns = [
        {
            title: 'Name',
            dataIndex: 'name',
            sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
        },
        {
            title: 'Numbers',
            dataIndex: 'campaign_reports_count',
            render: (_, record) =>
                record?.campaign_reports_count ??
                record?.send_config?.recipient_count ??
                0,
            sorter: (a, b) =>
                (a.campaign_reports_count || 0) - (b.campaign_reports_count || 0),
        },
        {
            title: 'Schedule Date',
            dataIndex: 'schedule_time',
            render: (_, record) => <span>{formatScheduleTime(record)}</span>,
            sorter: (a, b) =>
                dayjs(a.schedule_time || 0).valueOf() - dayjs(b.schedule_time || 0).valueOf(),
        },
        {
            title: 'Template Name',
            dataIndex: 'template_name',
            sorter: (a, b) => (a.template_name || '').localeCompare(b.template_name || ''),
        },
        {
            title: 'Mode',
            dataIndex: 'recipient_mode',
            render: (mode) => {
                const isInternational = mode === 'international';
                return (
                    <Tag color={isInternational ? 'geekblue' : 'default'}>
                        {isInternational ? 'International' : 'Domestic'}
                    </Tag>
                );
            },
        },
        {
            title: 'Format',
            dataIndex: 'template_format',
            render: (value) => value || '-',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            render: (_, record) => {
                const status = record?.status || record?.campaign_status || 'unknown';
                return <Tag color={getStatusColor(status)}>{status}</Tag>;
            },
            sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
        },
        ...(!filter
            ? [
                {
                    title: 'Created Date',
                    dataIndex: 'created_at',
                    key: 'created_at',
                    sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
                    render: (created_at) =>
                        created_at ? dayjs(created_at).format('DD/MM/YYYY') : '-',
                },
            ]
            : []),
        {
            title: 'Action',
            dataIndex: 'actions',
            render: (_, item) => (
                <div className="text-right d-flex">
                    <Tooltip title="View recipients">
                        <Button
                            type="primary"
                            className="mr-2"
                            icon={<EyeOutlined />}
                            size="small"
                            onClick={() => showModal(item.id)}
                        />
                    </Tooltip>
                    {isManageableSchedule(item.status) && (
                        <>
                            <Tooltip title="Reschedule">
                                <Button
                                    type="default"
                                    className="mr-2"
                                    icon={<ClockCircleOutlined />}
                                    size="small"
                                    onClick={() => showScheduleModel(item.id)}
                                />
                            </Tooltip>
                            {item.status === 'scheduled' && (
                                <Tooltip title="Pause campaign">
                                    <Button
                                        type="default"
                                        className="mr-2"
                                        icon={<PauseCircleOutlined />}
                                        size="small"
                                        onClick={() => showPauseConfirm(item.id)}
                                    />
                                </Tooltip>
                            )}
                            {item.status === 'paused' && (
                                <Tooltip title="Resume campaign">
                                    <Button
                                        type="default"
                                        className="mr-2"
                                        icon={<PlayCircleOutlined />}
                                        size="small"
                                        onClick={() => HandleResume(item.id)}
                                    />
                                </Tooltip>
                            )}
                            <Tooltip title="Stop campaign (cancel & refund)">
                                <Button
                                    danger
                                    className="mr-2"
                                    icon={<StopOutlined />}
                                    size="small"
                                    onClick={() => showStopConfirm(item.id)}
                                />
                            </Tooltip>
                        </>
                    )}
                </div>
            ),
        },
    ];

    function showStopConfirm(id) {
        confirm({
            title: 'Stop this scheduled campaign?',
            content: 'The campaign will be cancelled and credits will be refunded.',
            okText: 'Stop campaign',
            okType: 'danger',
            onOk() {
                return HandleCancel(id);
            },
        });
    }

    function showPauseConfirm(id) {
        confirm({
            title: 'Pause this scheduled campaign?',
            content: 'The campaign will not send until you resume it. Credits remain deducted.',
            okText: 'Pause',
            onOk() {
                return HandlePause(id);
            },
        });
    }

    const fetchCampaignRecipients = useCallback(async (id, page = 1, perPage = 50) => {
        setReportLoading(true);
        try {
            const response = await apiClient.get(`${api}campaign-reports/${id}`, {
                headers: {
                    Authorization: 'Bearer ' + authToken,
                },
                params: {
                    page,
                    per_page: perPage,
                },
            });
            setCampaignReport(response.data.campaign || []);
            if (response.data.pagination) {
                setReportPagination({
                    current: response.data.pagination.current_page,
                    pageSize: response.data.pagination.per_page,
                    total: response.data.pagination.total,
                });
            }
        } catch (error) {
            message.error('Failed to load campaign recipients');
        } finally {
            setReportLoading(false);
        }
    }, [api, authToken]);

    const showModal = async (id) => {
        setReportCampaignId(id);
        setOpen(true);
        await fetchCampaignRecipients(id, 1, reportPagination.pageSize);
    };

    const handleReportTableChange = (tablePagination) => {
        if (!reportCampaignId) return;
        fetchCampaignRecipients(
            reportCampaignId,
            tablePagination.current,
            tablePagination.pageSize
        );
    };

    const handleCancel = () => {
        setOpen(false);
        setCampaignReport([]);
        setReportCampaignId(null);
        setReportPagination({ current: 1, pageSize: 50, total: 0 });
    };

    const innerTableColumns = [
        {
            title: 'Number',
            dataIndex: 'mobile_number',
            sorter: (a, b) => (a.mobile_number || '').localeCompare(b.mobile_number || ''),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            render: (text) =>
                text === 'sent' ? (
                    <FontAwesomeIcon icon={faCheck} />
                ) : text === 'delivered' ? (
                    <FontAwesomeIcon icon={faCheckDouble} />
                ) : text === 'read' ? (
                    <FontAwesomeIcon icon={faCheckDouble} style={{ color: '#22af4b' }} />
                ) : text === 'pending' ? (
                    <Tag color="blue">pending</Tag>
                ) : (
                    <Tooltip title="Message failed or not yet sent">
                        <Button className="p-0 border-0">
                            <FontAwesomeIcon icon={faCircleExclamation} className="text-danger" />
                        </Button>
                    </Tooltip>
                ),
            sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
        },
        {
            title: 'Date',
            dataIndex: 'updated_at',
            sorter: (a, b) => dayjs(a.updated_at).valueOf() - dayjs(b.updated_at).valueOf(),
            render: (updated_at) =>
                updated_at ? dayjs(updated_at).format('DD/MM/YYYY HH:mm:ss') : '-',
        },
    ];

    const showScheduleModel = (id) => {
        setScheduleDate('');
        setScheduleTime('');
        setEditId(id);
        setOpenSchedule(true);
    };

    const handleCancelScheduleModel = () => {
        setOpenSchedule(false);
        setEditId('');
        setScheduleDate('');
        setScheduleTime('');
    };

    const HandleScheduleTime = (value) => {
        if (!value) {
            setScheduleDate('');
            setScheduleTime('');
            return;
        }

        const parsed = value.$d ? dayjs(value.$d) : dayjs(value);
        setScheduleDate(parsed.format('YYYY-M-D'));
        setScheduleTime(parsed.format('H:mm:00'));
    };

    const HandleSchedule = async () => {
        if (!scheduleDate || !scheduleTime) {
            message.error('Please select a schedule date and time.');
            return;
        }

        setRescheduleLoading(true);
        try {
            const campaignResponse = await apiClient.patch(
                `${api}campaigns/${editId}/reschedule`,
                {
                    schedule_date: scheduleDate,
                    schedule_time: scheduleTime,
                },
                {
                    headers: {
                        Authorization: 'Bearer ' + authToken,
                    },
                }
            );

            const successMessage =
                campaignResponse?.data?.data?.message || 'Campaign rescheduled successfully';
            handleCancelScheduleModel();
            GetCampaignData();
            message.success(successMessage);
        } catch (error) {
            message.error(
                error?.response?.data?.data?.message ||
                error?.response?.data?.message ||
                'Failed to reschedule campaign'
            );
        } finally {
            setRescheduleLoading(false);
        }
    };

    const HandlePause = async (id) => {
        try {
            const response = await apiClient.post(
                `${api}campaigns/${id}/pause-schedule`,
                {},
                {
                    headers: {
                        Authorization: 'Bearer ' + authToken,
                    },
                }
            );

            message.success(response?.data?.data?.message || 'Campaign paused');
            GetCampaignData();
        } catch (error) {
            message.error(
                error?.response?.data?.data?.message ||
                error?.response?.data?.message ||
                'Failed to pause campaign'
            );
            throw error;
        }
    };

    const HandleResume = async (id) => {
        try {
            const response = await apiClient.post(
                `${api}campaigns/${id}/resume-schedule`,
                {},
                {
                    headers: {
                        Authorization: 'Bearer ' + authToken,
                    },
                }
            );

            message.success(response?.data?.data?.message || 'Campaign resumed');
            GetCampaignData();
        } catch (error) {
            message.error(
                error?.response?.data?.data?.message ||
                error?.response?.data?.message ||
                'Failed to resume campaign'
            );
        }
    };

    const HandleCancel = async (id) => {
        try {
            const campaignResponse = await apiClient.post(
                `${api}campaigns/${id}/cancel-schedule`,
                {},
                {
                    headers: {
                        Authorization: 'Bearer ' + authToken,
                    },
                }
            );

            const successMessage =
                campaignResponse?.data?.data?.message || 'Scheduled campaign stopped';
            GetCampaignData();
            message.success(successMessage);
        } catch (error) {
            message.error(
                error?.response?.data?.data?.message ||
                error?.response?.data?.message ||
                'Failed to stop scheduled campaign'
            );
            throw error;
        }
    };

    return (
        <>
            <Modal
                title="Scheduled campaign recipients"
                open={open}
                onCancel={handleCancel}
                footer={null}
                width={900}
            >
                <Table
                    loading={reportLoading}
                    columns={innerTableColumns}
                    dataSource={campaignReport}
                    rowKey="id"
                    pagination={{
                        current: reportPagination.current,
                        pageSize: reportPagination.pageSize,
                        total: reportPagination.total,
                        showSizeChanger: true,
                        pageSizeOptions: ['25', '50', '100'],
                        showTotal: (total) => `Total ${total} recipients`,
                    }}
                    onChange={handleReportTableChange}
                />
            </Modal>

            <Modal
                title="Reschedule Campaign"
                open={openScheduleModel}
                destroyOnClose
                onCancel={handleCancelScheduleModel}
                footer={null}
            >
                <Result
                    status="success"
                    title="Select date and time"
                    extra={
                        <>
                            <DatePicker
                                disabledDate={(current) =>
                                    current && current < dayjs().startOf('day')
                                }
                                showTime={{ format: 'HH:mm' }}
                                format="YYYY-MM-DD HH:mm"
                                onChange={(value) => HandleScheduleTime(value)}
                                style={{ width: '100%', marginBottom: 16 }}
                            />
                            <Button
                                type="primary"
                                onClick={() => HandleSchedule()}
                                loading={rescheduleLoading}
                                disabled={!scheduleDate || !scheduleTime}
                            >
                                Reschedule Campaign
                            </Button>
                        </>
                    }
                />
            </Modal>

            <Table
                loading={listLoading}
                columns={tableColumns}
                dataSource={campaigns}
                rowKey="id"
                pagination={
                    isDashboardWidget
                        ? false
                        : {
                            current: listPagination.current,
                            pageSize: listPagination.pageSize,
                            total: listPagination.total,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50'],
                            showTotal: (total) => `Total ${total} campaigns`,
                        }
                }
                onChange={isDashboardWidget ? undefined : handleListTableChange}
            />
        </>
    );
};

export default ScheduleCampainTable;
